/**
 * 이벤트 API 모듈
 * 이벤트, 설문, 당첨자 관련 API 함수들을 제공합니다.
 */
export default function eventsApis($apiInstance, $store) {
    return {
        // ==================== 이벤트 관련 API ====================

        /**
         * 이벤트 목록 조회
         * @param {Object} params - 검색, 필터링, 페이지네이션 매개변수
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getEvents: async (params = {}, success, fail) => {
            $store.commit('setIsLoading', true);
            
            // params 객체를 query string으로 변환
            const queryParams = new URLSearchParams();
            Object.keys(params).forEach(key => {
                if (params[key] !== null && params[key] !== undefined) {
                    queryParams.append(key, params[key]);
                }
            });

            return $apiInstance.get(`/events-v2?${queryParams.toString()}`)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('이벤트 목록 조회 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 이벤트 하드 삭제 (영구 삭제)
         * @param {string} id - 이벤트 ID
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        hardDeleteEvent: async (id, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.delete(`/events-v2/${id}/hard`)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('이벤트 하드 삭제 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 고객 ID로 NFT 목록 조회
         * @param {string} customerId - 고객 ID
         * @param {Object} params - 페이지네이션 및 정렬 매개변수
         * @param {number} [params.page=1] - 페이지 번호
         * @param {number} [params.limit=10] - 페이지당 항목 수
         * @param {string} [params.sortBy='createAt'] - 정렬 기준 필드
         * @param {number} [params.sortOrder=-1] - 정렬 방향 (1: 오름차순, -1: 내림차순)
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getNftsByCustomerId: async (customerId, params = {}, success, fail) => {
            $store.commit('setIsLoading', true);
            
            // params 객체를 query string으로 변환
            const queryParams = new URLSearchParams();
            Object.keys(params).forEach(key => {
                if (params[key] !== null && params[key] !== undefined) {
                    queryParams.append(key, params[key]);
                }
            });

            return $apiInstance.get(`/events-v2/nfts/customer/${customerId}?${queryParams.toString()}`)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('고객 NFT 목록 조회 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 이벤트 주체자(병원/제조사) 목록 조회
         * @param {Object} params - 검색, 필터링, 페이지네이션 매개변수
         * @param {number} [params.page=1] - 페이지 번호
         * @param {number} [params.limit=10] - 페이지 크기
         * @param {string} [params.condition] - 검색어 (주체자명, 대표자명, 사업자번호 검색)
         * @param {string} [params.search] - 검색 필드 (name 등)
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getOrganizerList: async (params = {}, success, fail) => {
            $store.commit('setIsLoading', true);
            
            // 기본 파라미터 설정
            const requestParams = {
                page: params.page || 1,
                limit: params.limit || 10,
                condition: params.condition || '',
                search: params.search || 'name'
                // type 파라미터 제거 - 신규 API는 type 없이 모든 주체자 반환
            };
            
            // 통합 주체자 검색 엔드포인트 호출
            return $apiInstance.post('/companies/certi-admin/category-bind/all-organizers', requestParams)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('주체자 목록 조회 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },
        
        // 기존 getClientList는 호환성을 위해 getOrganizerList를 호출하도록 변경
        getClientList: async (params = {}, success, fail) => {
            console.warn('getClientList는 deprecated되었습니다. getOrganizerList를 사용하세요.');
            return eventsApis.getOrganizerList(params, success, fail);
        },

        /**
         * 이벤트 상세 조회
         * @param {string} id - 이벤트 ID
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getEventById: async (id, success, fail) => {
            $store.commit('setIsLoading', true);

            if (!id) {
                console.error('이벤트 상세 조회 실패: 이벤트 ID가 없습니다.');
                fail({ message: '이벤트 ID가 필요합니다', status: 400 });
                $store.commit('setIsLoading', false);
                return;
            }

            return $apiInstance.get(`/events-v2/${id}`)
                .then((response) => {
                    console.log('이벤트 상세 조회 성공:', response.data);
                    
                    // 응답 데이터 유효성 검증
                    if (!response.data || !response.data.data) {
                        console.warn('이벤트 상세 조회 결과가 비어있습니다.');
                    } else {
                        // 이벤트 정보 로깅 (디버깅용)
                        const eventInfo = response.data.data;
                        console.log(`이벤트 [${eventInfo._id || id}] "${eventInfo.eventTitle}" 조회 완료`);
                        
                        // 진행주체(organizers) 정보 확인
                        if (eventInfo.organizers && eventInfo.organizers.length > 0) {
                            console.log(`이벤트 진행주체: ${eventInfo.organizers.length}개 기관`);
                        }
                    }
                    
                    success(response);
                }, (error) => {
                    console.error('이벤트 상세 조회 실패:', error);
                    
                    // 오류 상황별 메시지 구체화
                    let errorMessage = '이벤트 정보를 불러오는 중 오류가 발생했습니다';
                    let statusCode = error.response ? error.response.status : 500;
                    
                    // 404 오류 처리
                    if (statusCode === 404) {
                        errorMessage = '해당 ID의 이벤트를 찾을 수 없습니다';
                    }
                    // 401, 403 권한 오류 처리
                    else if (statusCode === 401 || statusCode === 403) {
                        errorMessage = '이벤트 정보를 볼 수 있는 권한이 없습니다';
                    }
                    
                    fail({
                        message: errorMessage,
                        status: statusCode,
                        original: error
                    });
                })
                .finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 이벤트 생성
         * @param {Object} eventData - 생성할 이벤트 데이터
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        createEvent: async (eventData, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.post('/events-v2', eventData)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('이벤트 생성 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 이벤트 수정
         * @param {string} id - 이벤트 ID
         * @param {Object} eventData - 수정할 이벤트 데이터
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        updateEvent: async (id, eventData, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.put(`/events-v2/${id}`, eventData)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('이벤트 수정 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 이벤트 삭제 (소프트 딜리트)
         * @param {string} id - 이벤트 ID
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        deleteEvent: async (id, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.delete(`/events-v2/${id}`)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('이벤트 삭제 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 이벤트 참여자 대량 생성 상태 조회
         * @param {string} id - 이벤트 ID
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getBulkStatus: async (id, success, fail) => {
            $store.commit('setIsLoading', true);

            if (!id) {
                console.error('대량 생성 상태 조회 실패: 이벤트 ID가 없습니다.');
                fail({ message: '이벤트 ID가 필요합니다', status: 400 });
                $store.commit('setIsLoading', false);
                return;
            }

            return $apiInstance.get(`/events-v2/${id}/bulk-status`)
                .then((response) => {
                    console.log('대량 생성 상태 조회 성공:', response.data);
                    success(response);
                }, (error) => {
                    console.error('대량 생성 상태 조회 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 이벤트 복제
         * @param {string} id - 복제할 이벤트 ID
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        duplicateEvent: async (id, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.post(`/events-v2/duplicate/${id}`)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('이벤트 복제 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 이벤트 상태 변경
         * @param {string} id - 이벤트 ID
         * @param {string} status - 변경할 상태 ('preparing', 'ongoing', 'completed')
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        updateEventStatus: async (id, status, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.put(`/events-v2/${id}/status`, { status })
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('이벤트 상태 변경 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 예약 정보와 참여자 연결
         * @param {Object} linkData - 연결 데이터 (participantId, reservationId, score)
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        linkReservation: async (linkData, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.post('/events-v2/link-reservation', linkData)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('예약 정보 연결 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // ==================== 이벤트 메시지 관련 API ====================

        /**
         * 특정 이벤트의 대상자 목록 조회
         * @param {string} eventId - 이벤트 ID
         * @param {Object} params - 필터링 및 페이지네이션 매개변수
         * @param {number} [params.page] - 페이지 번호
         * @param {number} [params.limit] - 페이지당 결과 수
         * @param {string} [params.companyId] - 회사/병원 ID
         * @param {string} [params.gender] - 성별 필터 (MALE/FEMALE)
         * @param {number} [params.minAge] - 최소 연령
         * @param {number} [params.maxAge] - 최대 연령
         * @param {string} [params.channel] - 채널 (kakao, wechat 등)
         * @param {boolean} [params.hasNft] - 인증서 보유 여부
         * @param {number} [params.recentNftDays] - 최근 N일 이내 인증서 발급
         * @param {string} [params.medicalDept] - 의료 부서 (DENT: 치과, DERM: 피부과, PS: 성형외과 등)
         * @param {string} [params.sortBy] - 정렬 기준 필드
         * @param {string} [params.sortOrder] - 정렬 방향 (asc/desc)
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getEventTargets: async (eventId, params = {}, success, fail) => {
            $store.commit('setIsLoading', true);
            
            // params 객체를 query string으로 변환
            const queryParams = new URLSearchParams();
            Object.keys(params).forEach(key => {
                if (params[key] !== null && params[key] !== undefined) {
                    queryParams.append(key, params[key]);
                }
            });

            return $apiInstance.get(`/events-v2/target/targets/${eventId}?${queryParams.toString()}`)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('이벤트 대상자 목록 조회 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 이벤트 ID 없이 대상자 목록 조회
         * @param {Object} params - 필터링 및 페이지네이션 매개변수
         * @param {number} [params.page] - 페이지 번호
         * @param {number} [params.limit] - 페이지당 결과 수
         * @param {string} [params.companyId] - 회사/병원 ID
         * @param {string} [params.gender] - 성별 필터 (MALE/FEMALE)
         * @param {number} [params.minAge] - 최소 연령
         * @param {number} [params.maxAge] - 최대 연령
         * @param {string} [params.channel] - 채널 (kakao, wechat 등)
         * @param {boolean} [params.hasNft] - 인증서 보유 여부
         * @param {number} [params.recentNftDays] - 최근 N일 이내 인증서 발급
         * @param {string} [params.medicalDept] - 의료 부서 (DENT: 치과, DERM: 피부과, PS: 성형외과 등)
         * @param {string} [params.customerName] - 고객 이름 (부분 일치 검색)
         * @param {string} [params.customerMobile] - 고객 모바일 번호 (부분 일치 검색)
         * @param {string} [params.sortBy] - 정렬 기준 필드
         * @param {string} [params.sortOrder] - 정렬 방향 (asc/desc)
         * @param {Object} [config] - 요청 설정 옵션
         * @param {boolean} [config.showGlobalLoading=true] - 글로벌 로딩 표시 여부
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getTargets: async (params = {}, config = {}, success, fail) => {
            // config가 함수인 경우 (이전 API 호출 방식 지원)
            if (typeof config === 'function') {
                fail = success;
                success = config;
                config = {};
            }
            
            // params 객체를 query string으로 변환
            const queryParams = new URLSearchParams();
            Object.keys(params).forEach(key => {
                if (params[key] !== null && params[key] !== undefined) {
                    if (key === 'hasNft' && typeof params[key] === 'boolean') {
                        queryParams.append(key, params[key].toString());
                    } else {
                        queryParams.append(key, params[key]);
                    }
                }
            });

            return $apiInstance.get(`/events-v2/target/targets?${queryParams.toString()}`, {
                showGlobalLoading: config.showGlobalLoading !== false  // 기본값은 true
            })
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('대상자 목록 조회 실패:', error);
                    fail(error);
                });
        },

        /**
         * 알림톡/메시지 템플릿 목록 조회
         * @param {Object} params - 필터링 및 페이지네이션 매개변수
         * @param {number} [params.page] - 페이지 번호
         * @param {number} [params.limit] - 페이지당 결과 수
         * @param {string} [params.channel] - 채널 (kakao, wechat 등)
         * @param {string} [params.type] - 템플릿 타입 (event, notification, certificate 등)
         * @param {string} [params.keyword] - 검색 키워드
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getTemplates: async (params = {}, success, fail) => {
            $store.commit('setIsLoading', true);
            
            // params 객체를 query string으로 변환
            const queryParams = new URLSearchParams();
            Object.keys(params).forEach(key => {
                if (params[key] !== null && params[key] !== undefined) {
                    queryParams.append(key, params[key]);
                }
            });

            // 실제 API가 준비되지 않은 경우를 대비한 임시 처리
            // 실제 API가 구현되면 아래 주석된 코드를 활성화하고 임시 데이터 부분을 제거
            // return $apiInstance.get(`/events-v2/message/templates?${queryParams.toString()}`)
            //     .then((response) => {
            //         success(response);
            //     }, (error) => {
            //         console.error('템플릿 목록 조회 실패:', error);
            //         fail(error);
            //     }).finally(() => $store.commit('setIsLoading', false));

            // 임시 데이터로 응답 (API 개발 전까지 사용)
            setTimeout(() => {
                const templates = getMockTemplates(params);
                const total = templates.length;
                const page = params.page || 1;
                const limit = params.limit || 5;
                const start = (page - 1) * limit;
                const end = start + limit;
                const paginatedData = templates.slice(start, end);

                success({
                    data: {
                        data: paginatedData,
                        pagination: {
                            total,
                            page,
                            limit,
                            totalPages: Math.ceil(total / limit)
                        }
                    }
                });
                $store.commit('setIsLoading', false);
            }, 500);
        },

        /**
         * 알림톡 발송 요청 등록
         * @param {Object} data - 알림톡 발송 데이터
         * @param {Array<string>} data.customerIds - 고객 ID 배열
         * @param {Object} data.alimtalkTemplateBody - 알림톡 템플릿 내용
         * @param {Object} data.alimtalkTemplateBody.templateItem - 알림톡 템플릿 아이템 (비즈니스형 알림톡)
         * @param {Array} data.alimtalkTemplateBody.templateItem.list - 아이템 목록 (제목/설명 형식의 객체 배열)
         * @param {boolean} [data.hasNft] - 인증서 보유 여부 필터 (true: 보유, false: 미보유, 미지정: 필터링 안함)
         * @param {string} [data.eventId] - 메시지 발송과 관련된 이벤트 ID (선택적)
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         * 
         * @returns {Object} 응답 데이터
         * @returns {boolean} response.success - 성공 여부
         * @returns {string} response.message - 응답 메시지
         * @returns {Object} response.stats - 통계 정보
         * @returns {number} response.stats.total - 총 대상자 수
         * @returns {number} response.stats.success - 등록 성공 수
         * @returns {number} response.stats.fail - 등록 실패 수
         * @returns {Array<string>} response.processedIds - 처리된 고객 ID 목록
         * @returns {Array<string>} response.attendeeIds - 생성된 참석자 ID 목록
         * @returns {string} response.eventId - 이벤트 ID
         * @returns {string} response.status - 현재 상태 ('requested')
         */
        sendAlimtalk: async (data, success, fail) => {
            $store.commit('setIsLoading', true);

            // 템플릿 아이템 존재 여부 확인 및 디버깅
            if (data && data.alimtalkTemplateBody && data.alimtalkTemplateBody.templateItem) {
                console.log('알림톡 API 호출 - templateItem 포함:', 
                    data.alimtalkTemplateBody.templateItem);
            } else {
                console.warn('알림톡 API 호출 - templateItem 누락');
            }
            
            // hasNft 필터 디버깅
            if (data.hasNft !== undefined) {
                console.log('알림톡 API 호출 - hasNft 필터 적용:', data.hasNft);
            }
            
            // eventId 디버깅
            if (data.eventId) {
                console.log('알림톡 API 호출 - eventId 포함:', data.eventId);
            }

            return $apiInstance.post('/events-v2/message/register-alimtalk-request', data)
                .then((response) => {
                    console.log('알림톡 발송 API 응답:', response.data);
                    success(response);
                }, (error) => {
                    console.error('알림톡 발송 요청 등록 실패:', error);
                    
                    // 서버에서 전달한 상세 오류 메시지 추출 시도
                    let errorMessage = '알림톡 발송 중 오류가 발생했습니다';
                    let errorDetail = '';
                    
                    if (error.response) {
                        // 응답이 있는 경우
                        if (error.response.data) {
                            if (typeof error.response.data === 'object') {
                                // 객체인 경우, 구조에 따라 메시지 추출
                                if (error.response.data.message) {
                                    errorMessage = error.response.data.message;
                                    // 상세 메시지가 있는 경우 추가 정보로 포함
                                    if (error.response.data.error) {
                                        errorDetail = error.response.data.error;
                                    }
                                } else if (error.response.data.error) {
                                    errorMessage = error.response.data.error;
                                }
                            } else if (typeof error.response.data === 'string') {
                                // 문자열인 경우 그대로 사용
                                errorMessage = error.response.data;
                            }
                        }
                    } else if (error.request) {
                        // 요청은 보냈지만 응답을 받지 못한 경우
                        errorMessage = '서버에서 응답이 없습니다. 네트워크 연결을 확인해주세요.';
                    } else {
                        // 요청 설정 시 오류 발생한 경우
                        errorMessage = `요청 설정 중 오류가 발생했습니다: ${error.message}`;
                    }
                    
                    // 추출한 오류 메시지를 포함해서 에러 객체 전달
                    fail({
                        original: error,
                        message: errorMessage,
                        detail: errorDetail,
                        status: error.response ? error.response.status : 500
                    });
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 이벤트 참가자 목록 조회
         * @param {string} eventId - 이벤트 ID
         * @param {Object} params - 페이지네이션 매개변수
         * @param {number} [params.page] - 페이지 번호
         * @param {number} [params.limit] - 페이지당 결과 수
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getEventAttendees: async (eventId, params = {}, success, fail) => {
            $store.commit('setIsLoading', true);
            
            // params 객체를 query string으로 변환
            const queryParams = new URLSearchParams();
            Object.keys(params).forEach(key => {
                if (params[key] !== null && params[key] !== undefined) {
                    queryParams.append(key, params[key]);
                }
            });

            return $apiInstance.get(`/events-v2/attendees/${eventId}/attendees?${queryParams.toString()}`)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('이벤트 참가자 목록 조회 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // ==================== 설문 관련 API ====================

        /**
         * 설문 응답 저장
         * @param {Object} responseData - 설문 응답 데이터
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        saveSurveyResponse: async (responseData, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.post('/events-v2/surveys/responses', responseData)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('설문 응답 저장 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 이벤트별 설문 응답 목록 조회
         * @param {string} eventId - 이벤트 ID
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getSurveyResponsesByEvent: async (eventId, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.get(`/events-v2/surveys/responses/event/${eventId}`)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('설문 응답 목록 조회 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 설문 응답 상세 조회
         * @param {string} id - 설문 응답 ID
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getSurveyResponseById: async (id, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.get(`/events-v2/surveys/responses/${id}`)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('설문 응답 상세 조회 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 참여자별 설문 응답 조회
         * @param {string} participantId - 참여자 ID
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getSurveyResponseByParticipant: async (participantId, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.get(`/events-v2/surveys/responses/participant/${participantId}`)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('참여자별 설문 응답 조회 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 설문 응답 통계 조회
         * @param {string} eventId - 이벤트 ID
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getSurveyStatistics: async (eventId, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.get(`/events-v2/surveys/statistics/${eventId}`)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('설문 통계 조회 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 미완료 설문 응답 정리 (관리자용)
         * @param {number} hoursOld - 지정 시간(시간) 이전 데이터 삭제
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        cleanupIncompleteSurveys: async (hoursOld = 24, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.delete('/events-v2/surveys/cleanup', { params: { hoursOld } })
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('미완료 설문 응답 정리 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // ==================== 당첨자 관련 API ====================

        /**
         * 당첨자 정보 저장
         * @param {Object} winnerData - 당첨자 정보
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        createWinner: async (winnerData, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.post('/events-v2/winners', winnerData)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('당첨자 정보 저장 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 이벤트별 당첨자 목록 조회
         * @param {string} eventId - 이벤트 ID
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getWinnersByEvent: async (eventId, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.get(`/events-v2/winners/event/${eventId}`)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('당첨자 목록 조회 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 당첨자 상세 정보 조회
         * @param {string} id - 당첨자 ID
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getWinnerById: async (id, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.get(`/events-v2/winners/${id}`)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('당첨자 상세 조회 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 배송 정보 업데이트
         * @param {string} id - 당첨자 ID
         * @param {Object} deliveryInfo - 배송 정보
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        updateDeliveryInfo: async (id, deliveryInfo, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.put(`/events-v2/winners/${id}/delivery-info`, { deliveryInfo })
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('배송 정보 업데이트 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 배송 상태 업데이트
         * @param {string} id - 당첨자 ID
         * @param {Object} statusData - 상태 데이터 (deliveryStatus, trackingNumber, courierCompany)
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        updateDeliveryStatus: async (id, statusData, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.put(`/events-v2/winners/${id}/delivery-status`, statusData)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('배송 상태 업데이트 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 당첨자 무작위 선정
         * @param {Object} selectData - 선정 데이터 (eventId, prizeId, count)
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        selectRandomWinners: async (selectData, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.post('/events-v2/winners/select/random', selectData)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('당첨자 무작위 선정 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 당첨자 참여도 기반 선정
         * @param {Object} selectData - 선정 데이터 (eventId, prizeId, count)
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        selectParticipationBasedWinners: async (selectData, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.post('/events-v2/winners/select/participation', selectData)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('당첨자 참여도 기반 선정 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 알림톡 템플릿 목록 조회 (비즈메시지 Resell API 사용)
         * @param {Object} params - 검색, 필터링, 페이지네이션 매개변수
         * @param {number} [params.page=1] - 페이지 번호
         * @param {number} [params.rows=10] - 페이지 크기
         * @param {string} [params.startDate] - 시작일 (YYYY-MM-DD)
         * @param {string} [params.endDate] - 종료일 (YYYY-MM-DD)
         * @param {string} [params.kepStatus] - 검수 상태 (N:검수 전, I:검수 중, O:승인, R:반려)
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getAlimtalkResellTemplates: async (params = {}, success, fail) => {
            $store.commit('setIsLoading', true);
            
            // 파라미터 복사 (원본 변경 방지)
            const apiParams = { ...params };
            
            // startDate 기본값 설정 (파라미터에 없는 경우만)
            if (!apiParams.startDate) {
                apiParams.startDate = '2023-01-01';
            }
            
            // params 객체를 query string으로 변환
            const queryParams = new URLSearchParams();
            Object.keys(apiParams).forEach(key => {
                if (apiParams[key] !== null && apiParams[key] !== undefined) {
                    queryParams.append(key, apiParams[key]);
                }
            });

            return $apiInstance.get(`/bizmsg/resell/templates?${queryParams.toString()}`)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('비즈메시지 템플릿 목록 조회 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 알림톡 템플릿 상세 조회 (비즈메시지 Resell API 사용)
         * @param {string} templateCode - 템플릿 코드
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getAlimtalkResellTemplate: async (templateCode, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.get(`/bizmsg/resell/templates/${templateCode}`)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('비즈메시지 템플릿 상세 조회 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 이벤트 참여자 활동 원시데이터 조회
         * @param {string} eventId - 이벤트 ID
         * @param {Object} params - 조회 파라미터
         * @param {number} [params.limit=50] - 페이지당 항목 수
         * @param {number} [params.page=1] - 페이지 번호
         * @param {string} [params.customerId] - 특정 고객 ID로 필터링
         * @param {string} [params.activityType] - 활동 유형으로 필터링
         * @param {string} [params.startDate] - 시작일 (ISO format)
         * @param {string} [params.endDate] - 종료일 (ISO format)
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         * @returns {Promise} 활동 데이터 조회 결과
         */
        getAttendeeActivities: async (eventId, params = {}, success, fail) => {
            $store.commit('setIsLoading', true);

            // 기본 파라미터 설정
            const requestParams = {
                limit: params.limit || 50,
                page: params.page || 1,
                ...params
            };

            // params 객체를 query string으로 변환
            const queryParams = new URLSearchParams();
            Object.keys(requestParams).forEach(key => {
                if (requestParams[key] !== null && requestParams[key] !== undefined) {
                    queryParams.append(key, requestParams[key]);
                }
            });

            return $apiInstance.get(`/events-v2/attendees/${eventId}/activities/raw?${queryParams.toString()}`)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('활동 데이터 조회 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 사용자별 활동 데이터 조회
         * @param {string} eventId - 이벤트 ID
         * @param {Object} params - 조회 파라미터
         * @param {number} [params.page=1] - 페이지 번호
         * @param {number} [params.limit=10] - 페이지당 항목 수
         * @param {string} [params.sortBy='totalActivities'] - 정렬 기준
         * @param {string} [params.sortOrder='desc'] - 정렬 방향
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         * @returns {Promise} 사용자별 활동 데이터 조회 결과
         */
        getAttendeeActivitiesByUser: async (eventId, params = {}, success, fail) => {
            $store.commit('setIsLoading', true);

            // 기본 파라미터 설정
            const requestParams = {
                page: params.page || 1,
                limit: params.limit || 10,
                sortBy: params.sortBy || 'totalActivities',
                sortOrder: params.sortOrder || 'desc',
                ...params
            };

            // params 객체를 query string으로 변환
            const queryParams = new URLSearchParams();
            Object.keys(requestParams).forEach(key => {
                if (requestParams[key] !== null && requestParams[key] !== undefined) {
                    queryParams.append(key, requestParams[key]);
                }
            });

            return $apiInstance.get(`/events-v2/attendees/${eventId}/activities/by-user?${queryParams.toString()}`)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('사용자별 활동 데이터 조회 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 이벤트 참석자 정보 조회
         * @param {string} eventId - 이벤트 ID
         * @param {string} customerId - 고객 ID
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         * @returns {Promise} 이벤트 참석자 정보 조회 결과
         */
        getEventAttendeeByIds: async (eventId, customerId, success, fail) => {
            $store.commit('setIsLoading', true);

            // 유효성 검증
            if (!eventId || !customerId) {
                console.error('이벤트 ID 또는 고객 ID가 필요합니다');
                fail({ message: '이벤트 ID 또는 고객 ID가 필요합니다' });
                $store.commit('setIsLoading', false);
                return;
            }

            return $apiInstance.get(`/events-v2/attendees/${eventId}/attendee/${customerId}`)
                .then((response) => {
                    console.log('이벤트 참석자 정보 조회 성공:', response.data);
                    success(response);
                }, (error) => {
                    console.error('이벤트 참석자 정보 조회 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 특정 이벤트의 참석자 목록 조회
         * @param {string} eventId - 이벤트 ID
         * @param {Object} params - 검색, 필터링, 페이지네이션 매개변수
         * @param {number} [params.page=1] - 페이지 번호
         * @param {number} [params.limit=20] - 페이지당 항목 수
         * @param {string} [params.status] - 참석자 상태 필터 ('requested', 'sent', 'viewed', 'clicked', 'failed')
         * @param {string} [params.sort='participatedAt'] - 정렬 기준 필드
         * @param {string} [params.order='desc'] - 정렬 방향 ('asc', 'desc')
         * @param {string} [params.search] - 이름 또는 전화번호 검색어
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         * @returns {Promise} 참석자 목록 조회 결과
         */
        getEventAttendeesList: async (eventId, params = {}, success, fail) => {
            // 글로벌 로딩 비활성화 - 참석자 목록 새로고침 시에는 전역 로딩 스피너를 표시하지 않음

            // 유효성 검증
            if (!eventId) {
                console.error('이벤트 ID가 필요합니다');
                fail({ message: '이벤트 ID가 필요합니다' });
                return;
            }

            // params 객체를 query string으로 변환
            const queryParams = new URLSearchParams();
            Object.keys(params).forEach(key => {
                if (params[key] !== null && params[key] !== undefined) {
                    queryParams.append(key, params[key]);
                }
            });

            return $apiInstance.get(`/events-v2/attendees/${eventId}/attendees?${queryParams.toString()}`, {
                showGlobalLoading: false  // 글로벌 로딩 비활성화
            })
                .then((response) => {
                    console.log('이벤트 참석자 목록 조회 성공:', response.data);
                    success(response);
                }, (error) => {
                    console.error('이벤트 참석자 목록 조회 실패:', error);
                    fail(error);
                });
        },

        // ==================== 블랙리스트 환자 관리 API ====================

        /**
         * 블랙리스트 환자 목록 조회/검색
         * @param {Object} params - 검색, 필터링, 페이지네이션 매개변수
         * @param {string} [params.type] - 검색 조건 (patientName, phoneNumber, hospitalName, businessNumber)
         * @param {string} [params.keyword] - 검색어
         * @param {number} [params.page=1] - 페이지 번호
         * @param {number} [params.pageSize=10] - 페이지당 항목 수
         * @param {string} [params.hospitalId] - 병원 ID로 필터링
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getBlacklistPatients: async (params = {}, success, fail) => {
            $store.commit('setIsLoading', true);
            
            // params 객체를 query string으로 변환
            const queryParams = new URLSearchParams();
            Object.keys(params).forEach(key => {
                if (params[key] !== null && params[key] !== undefined) {
                    queryParams.append(key, params[key]);
                }
            });

            return $apiInstance.get(`/events-v2-blacklist-patients?${queryParams.toString()}`)
                .then((response) => {
                    console.log('블랙리스트 환자 목록 조회 성공:', response.data);
                    success(response);
                }, (error) => {
                    console.error('블랙리스트 환자 목록 조회 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 단건 블랙리스트 환자 추가
         * @param {Object} patientData - 환자 데이터
         * @param {string} patientData.patientName - 환자명 (필수)
         * @param {string} patientData.phoneNumber - 전화번호 (필수)
         * @param {string} [patientData.hospitalId] - 병원 ID
         * @param {string} [patientData.hospitalName] - 병원명
         * @param {string} [patientData.businessNumber] - 사업자번호
         * @param {string} [patientData.reason] - 등록 사유
         * @param {string} [patientData.notes] - 비고
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        addBlacklistPatient: async (patientData, success, fail) => {
            $store.commit('setIsLoading', true);

            // 필수 필드 검증
            if (!patientData.patientName || !patientData.phoneNumber) {
                console.error('환자명과 전화번호는 필수입니다');
                fail({ message: '환자명과 전화번호는 필수입니다' });
                $store.commit('setIsLoading', false);
                return;
            }

            return $apiInstance.post('/events-v2-blacklist-patients', patientData)
                .then((response) => {
                    console.log('블랙리스트 환자 추가 성공:', response.data);
                    success(response);
                }, (error) => {
                    console.error('블랙리스트 환자 추가 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * Excel 파일 업로드로 블랙리스트 환자 일괄 추가
         * @param {File} file - Excel 파일
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        uploadBlacklistPatients: async (file, success, fail) => {
            $store.commit('setIsLoading', true);

            if (!file) {
                console.error('업로드할 파일이 없습니다');
                fail({ message: '업로드할 파일이 없습니다' });
                $store.commit('setIsLoading', false);
                return;
            }

            const formData = new FormData();
            formData.append('file', file);

            return $apiInstance.post('/events-v2-blacklist-patients/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
                .then((response) => {
                    console.log('Excel 업로드 성공:', response.data);
                    success(response);
                }, (error) => {
                    console.error('Excel 업로드 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 병원별 환자 일괄 추가
         * @param {Object} hospitalData - 병원 데이터
         * @param {string} hospitalData.hospitalId - 병원 ID (필수)
         * @param {string} [hospitalData.reason] - 등록 사유
         * @param {string} [hospitalData.notes] - 비고
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        addHospitalBlacklistPatients: async (hospitalData, success, fail) => {
            $store.commit('setIsLoading', true);

            if (!hospitalData.hospitalId) {
                console.error('병원 ID가 필요합니다');
                fail({ message: '병원 ID가 필요합니다' });
                $store.commit('setIsLoading', false);
                return;
            }

            return $apiInstance.post('/events-v2-blacklist-patients/hospital-bulk', hospitalData)
                .then((response) => {
                    console.log('병원별 환자 일괄 추가 성공:', response.data);
                    success(response);
                }, (error) => {
                    console.error('병원별 환자 일괄 추가 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 병원 검색
         * @param {string} keyword - 검색 키워드 (병원명 또는 사업자번호)
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        searchHospitalsForBlacklist: async (keyword, success, fail) => {
            $store.commit('setIsLoading', true);

            if (!keyword || !keyword.trim()) {
                console.error('검색 키워드가 필요합니다');
                fail({ message: '검색 키워드가 필요합니다' });
                $store.commit('setIsLoading', false);
                return;
            }

            const queryParams = new URLSearchParams();
            queryParams.append('keyword', keyword.trim());

            return $apiInstance.get(`/events-v2-blacklist-patients/hospitals/search?${queryParams.toString()}`)
                .then((response) => {
                    console.log('병원 검색 성공:', response.data);
                    success(response);
                }, (error) => {
                    console.error('병원 검색 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 블랙리스트 여부 확인
         * @param {string} phoneNumber - 전화번호
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        checkBlacklistStatus: async (phoneNumber, success, fail) => {
            $store.commit('setIsLoading', true);

            if (!phoneNumber) {
                console.error('전화번호가 필요합니다');
                fail({ message: '전화번호가 필요합니다' });
                $store.commit('setIsLoading', false);
                return;
            }

            return $apiInstance.get(`/events-v2-blacklist-patients/check/${encodeURIComponent(phoneNumber)}`)
                .then((response) => {
                    console.log('블랙리스트 여부 확인 성공:', response.data);
                    success(response);
                }, (error) => {
                    console.error('블랙리스트 여부 확인 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 참여자 쿠폰 제거
         * @param {string} eventId - 이벤트 ID
         * @param {Array<string>} attendeeIds - 참여자 ID 배열
         * @param {string} couponNumber - 쿠폰 번호
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        removeCoupons: async (eventId, attendeeIds, couponNumber, success, fail) => {
            $store.commit('setIsLoading', true);

            if (!eventId || !attendeeIds || !couponNumber) {
                console.error('이벤트 ID, 참여자 ID, 쿠폰 번호가 필요합니다');
                fail({ message: '필수 정보가 누락되었습니다' });
                $store.commit('setIsLoading', false);
                return;
            }

            const data = {
                attendeeIds: attendeeIds,
                couponNumber: couponNumber
            };

            return $apiInstance.delete(`/events-v2/prizes/${eventId}/remove-coupons`, { data })
                .then((response) => {
                    console.log('쿠폰 제거 성공:', response.data);
                    success(response);
                }, (error) => {
                    console.error('쿠폰 제거 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 블랙리스트 환자 제거 (단건)
         * @param {string} patientId - 환자 ID
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        removeBlacklistPatient: async (patientId, success, fail) => {
            $store.commit('setIsLoading', true);

            if (!patientId) {
                console.error('환자 ID가 필요합니다');
                fail({ message: '환자 ID가 필요합니다' });
                $store.commit('setIsLoading', false);
                return;
            }

            return $apiInstance.delete(`/events-v2-blacklist-patients/${patientId}`)
                .then((response) => {
                    console.log('블랙리스트 환자 제거 성공:', response.data);
                    success(response);
                }, (error) => {
                    console.error('블랙리스트 환자 제거 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 블랙리스트 환자 일괄 삭제
         * @param {Array<string>} patientIds - 환자 ID 배열
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        bulkRemoveBlacklistPatients: async (patientIds, success, fail) => {
            $store.commit('setIsLoading', true);

            if (!patientIds || patientIds.length === 0) {
                console.error('삭제할 환자 ID가 필요합니다');
                fail({ message: '삭제할 환자 ID가 필요합니다' });
                $store.commit('setIsLoading', false);
                return;
            }

            return $apiInstance.delete('/events-v2-blacklist-patients/bulk', {
                data: { ids: patientIds }
            })
                .then((response) => {
                    console.log('블랙리스트 환자 일괄 삭제 성공:', response.data);
                    success(response);
                }, (error) => {
                    console.error('블랙리스트 환자 일괄 삭제 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 블랙리스트 환자 엑셀 다운로드
         * @param {Object} params - 검색 조건 (현재 검색 필터와 동일)
         * @param {string} [params.type] - 검색 타입 (patientName, phoneNumber, hospitalName, businessNumber)
         * @param {string} [params.keyword] - 검색 키워드
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        downloadBlacklistPatientsExcel: async (params = {}, success, fail) => {
            $store.commit('setIsLoading', true);
            
            try {
                // params 객체를 query string으로 변환
                const queryParams = new URLSearchParams();
                Object.keys(params).forEach(key => {
                    if (params[key] !== null && params[key] !== undefined) {
                        queryParams.append(key, params[key]);
                    }
                });

                const response = await $apiInstance.get(`/events-v2-blacklist-patients/download/excel?${queryParams.toString()}`, {
                    responseType: 'blob' // 파일 다운로드를 위한 blob 타입
                });

                // 파일명 생성 (현재 날짜 포함)
                const now = new Date();
                const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD 형식
                const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, ''); // HHMMSS 형식
                const fileName = `후기이벤트_발송 제외 리스트_${dateStr}_${timeStr}.xlsx`;

                // Blob을 이용한 파일 다운로드
                const blob = new Blob([response.data], { 
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
                });
                
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                console.log('블랙리스트 환자 엑셀 다운로드 성공');
                success({ fileName, recordCount: response.headers['x-record-count'] || 0 });
                
            } catch (error) {
                console.error('블랙리스트 환자 엑셀 다운로드 실패:', error);
                fail(error);
            } finally {
                $store.commit('setIsLoading', false);
            }
        },
        
        /**
         * 기참여자 중복 체크 API
         * @param {string} eventId - 이벤트 ID
         * @param {Object} data - 요청 데이터
         * @param {Array<string>} data.customerIds - 고객 ID 배열
         * @param {boolean} [data.excludeBlacklist=true] - 블랙리스트 제외 여부
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        checkDuplicateAttendees: async (eventId, data, success, fail) => {
            $store.commit('setIsLoading', true);
            
            if (!eventId) {
                console.error('이벤트 ID가 필요합니다');
                fail({ message: '이벤트 ID가 필요합니다' });
                $store.commit('setIsLoading', false);
                return;
            }
            
            if (!data.customerIds || data.customerIds.length === 0) {
                console.error('고객 ID 목록이 필요합니다');
                fail({ message: '고객 ID 목록이 필요합니다' });
                $store.commit('setIsLoading', false);
                return;
            }
            
            return $apiInstance.post(`/events-v2/attendees/${eventId}/check-duplicates`, data)
                .then((response) => {
                    console.log('기참여자 중복 체크 성공:', response.data);
                    success(response);
                }, (error) => {
                    console.error('기참여자 중복 체크 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // ==================== 이벤트 참여자 활동 필터링 API ====================

        /**
         * 상태별 이벤트 목록 조회
         * @param {Object} params - 검색, 필터링, 페이지네이션 매개변수
         * @param {string} [params.status='ongoing'] - 이벤트 상태 (ongoing: 진행중, all: 전체)
         * @param {number} [params.page=1] - 페이지 번호
         * @param {number} [params.limit=100] - 페이지당 항목 수
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getEventsWithStatus: async (params = {}, success, fail) => {
            $store.commit('setIsLoading', true);
            
            // 기본값 설정
            const apiParams = {
                status: 'ongoing',
                page: 1,
                limit: 100,
                ...params
            };
            
            // params 객체를 query string으로 변환
            const queryParams = new URLSearchParams();
            Object.keys(apiParams).forEach(key => {
                if (apiParams[key] !== null && apiParams[key] !== undefined) {
                    queryParams.append(key, apiParams[key]);
                }
            });

            return $apiInstance.get(`/events-v2?${queryParams.toString()}`)
                .then((response) => {
                    console.log('상태별 이벤트 목록 조회 성공:', response.data);
                    success(response);
                }, (error) => {
                    console.error('상태별 이벤트 목록 조회 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 인증서 동기화 시작
         * @param {string} eventId - 이벤트 ID
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        startCertificateSync: async (eventId, success, fail) => {
            // 동기화 시작 시에도 로딩 표시하지 않음 (버튼 자체에 로딩 상태가 있음)
            return $apiInstance.post(`/api/events-v2/certificate-sync/event/${eventId}`, {}, {
                showGlobalLoading: false  // 전역 로딩 비활성화
            })
                .then((response) => {
                    console.log('인증서 동기화 시작 성공:', response.data);
                    success(response);
                }, (error) => {
                    console.error('인증서 동기화 시작 실패:', error);
                    fail(error);
                });
        },

        /**
         * 인증서 동기화 상태 조회
         * @param {string} eventId - 이벤트 ID
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getCertificateSyncStatus: async (eventId, success, fail) => {
            // 폴링 시에는 로딩 상태 표시하지 않음 (화면 깜빡임 방지)
            return $apiInstance.get(`/api/events-v2/certificate-sync/event/${eventId}/status`, {
                showGlobalLoading: false  // 전역 로딩 비활성화
            })
                .then((response) => {
                    success(response);
                }, (error) => {
                    // 폴링 중 에러는 조용히 처리
                    if (fail) fail(error);
                });
        },

        /**
         * 당첨자 알림 발송
         * @param {string} eventId - 이벤트 ID
         * @param {Object} data - 발송 데이터
         * @param {string} [data.messageType='ALIM_TALK'] - 메시지 타입
         * @param {Array<string>} data.attendeeIds - 참여자 ID 배열
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        sendWinnerNotifications: async (eventId, data, success, fail) => {
            $store.commit('setIsLoading', true);

            if (!eventId) {
                console.error('이벤트 ID가 필요합니다');
                fail({ message: '이벤트 ID가 필요합니다' });
                $store.commit('setIsLoading', false);
                return;
            }

            if (!data.attendeeIds || data.attendeeIds.length === 0) {
                console.error('참여자 ID가 필요합니다');
                fail({ message: '참여자 ID가 필요합니다' });
                $store.commit('setIsLoading', false);
                return;
            }

            const requestData = {
                messageType: data.messageType || 'ALIM_TALK',
                attendeeIds: data.attendeeIds
            };

            return $apiInstance.post(`/events-v2/message/${eventId}/send-winner-notifications`, requestData)
                .then((response) => {
                    console.log('당첨자 알림 발송 성공:', response.data);
                    success(response);
                }, (error) => {
                    console.error('당첨자 알림 발송 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 활동 유형으로 필터링된 참여자 목록 조회
         * @param {string} eventId - 이벤트 ID (선택적 - 전체 이벤트 대상자 조회 시 null 가능)
         * @param {Object} params - 검색, 필터링, 페이지네이션 매개변수
         * @param {Array<string>} [params.activityTypes] - 활동 유형 배열 (예: ['랜딩', '리뷰작성', 'message_sent'])
         * @param {string} [params.dateFrom] - 시작 날짜 (YYYY-MM-DD)
         * @param {string} [params.dateTo] - 종료 날짜 (YYYY-MM-DD)
         * @param {number} [params.page=1] - 페이지 번호
         * @param {number} [params.limit=20] - 페이지당 항목 수
         * @param {string} [params.search] - 검색어 (이름, 전화번호)
         * @param {string} [params.sort='participatedAt'] - 정렬 기준 필드
         * @param {string} [params.order='desc'] - 정렬 방향 (asc, desc)
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        getEventAttendeesWithActivities: async (eventId, params = {}, success, fail) => {
            // 글로벌 로딩 표시 여부 (기본: false - 실시간 필터링 시 UX 개선)
            const showLoading = params.showGlobalLoading !== false;
            if (showLoading) {
                $store.commit('setIsLoading', true);
            }
            
            // 기본값 설정
            const apiParams = {
                page: 1,
                limit: 20,
                sort: 'participatedAt',
                order: 'desc',
                ...params
            };
            
            // activityTypes 배열 처리
            if (apiParams.activityTypes && Array.isArray(apiParams.activityTypes)) {
                // 배열을 쉼표로 구분된 문자열로 변환
                apiParams.activityTypes = apiParams.activityTypes.join(',');
            }
            
            // params 객체를 query string으로 변환
            const queryParams = new URLSearchParams();
            Object.keys(apiParams).forEach(key => {
                if (apiParams[key] !== null && apiParams[key] !== undefined && key !== 'showGlobalLoading') {
                    queryParams.append(key, apiParams[key]);
                }
            });

            // API 엔드포인트 결정
            const endpoint = eventId 
                ? `/events-v2/attendees/${eventId}/attendees?${queryParams.toString()}`
                : `/events-v2/attendees?${queryParams.toString()}`;

            return $apiInstance.get(endpoint, {
                showGlobalLoading: showLoading
            })
                .then((response) => {
                    console.log('활동 필터링 참여자 목록 조회 성공:', response.data);
                    success(response);
                }, (error) => {
                    console.error('활동 필터링 참여자 목록 조회 실패:', error);
                    fail(error);
                }).finally(() => {
                    if (showLoading) {
                        $store.commit('setIsLoading', false);
                    }
                });
        },

        /**
         * 친구톡 이미지 업로드 (URL 방식)
         * @param {string} imageUrl - 업로드할 이미지 URL
         * @param {boolean} isWide - 와이드 이미지 여부 (기본값: false)
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         * @returns {Promise} 업로드된 이미지 URL
         */
        uploadFriendtalkImage: async (imageUrl, isWide = false, success, fail) => {
            $store.commit('setIsLoading', true);

            // 유효성 검증
            if (!imageUrl) {
                console.error('이미지 URL이 필요합니다');
                fail({ message: '이미지 URL이 필요합니다' });
                $store.commit('setIsLoading', false);
                return;
            }

            // URL 유효성 검증
            try {
                new URL(imageUrl);
            } catch (e) {
                console.error('유효하지 않은 URL입니다:', imageUrl);
                fail({ message: '유효하지 않은 URL입니다' });
                $store.commit('setIsLoading', false);
                return;
            }

            const data = {
                imageUrl: imageUrl,
                isWide: isWide
            };

            return $apiInstance.post('/integrations/friendtalk/image/upload', data)
                .then((response) => {
                    console.log('친구톡 이미지 업로드 성공:', response.data);
                    if (response.data.success && response.data.uploadedUrl) {
                        success(response.data.uploadedUrl);
                    } else {
                        fail({ message: response.data.error || '이미지 업로드 실패' });
                    }
                }, (error) => {
                    console.error('친구톡 이미지 업로드 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 친구톡 이미지 파일 직접 업로드
         * @param {File} file - 업로드할 이미지 파일
         * @param {boolean} isWide - 와이드 이미지 여부 (기본값: false)
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         * @returns {Promise} 업로드된 이미지 URL
         */
        uploadFriendtalkImageFile: async (file, isWide = false, success, fail) => {
            $store.commit('setIsLoading', true);

            // 유효성 검증
            if (!file) {
                console.error('이미지 파일이 필요합니다');
                fail({ message: '이미지 파일이 필요합니다' });
                $store.commit('setIsLoading', false);
                return;
            }

            const formData = new FormData();
            formData.append('file', file);

            // isWide를 쿼리 파라미터로 전달
            const url = `/integrations/friendtalk/image/upload-file?isWide=${isWide}`;

            return $apiInstance.post(url, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
                .then((response) => {
                    console.log('친구톡 이미지 파일 업로드 성공:', response.data);
                    if (response.data.success && response.data.uploadedUrl) {
                        success(response.data.uploadedUrl);
                    } else {
                        fail({ message: response.data.error || '이미지 업로드 실패' });
                    }
                }, (error) => {
                    console.error('친구톡 이미지 파일 업로드 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 참여정보 초기화 API
         * @param {string} eventId - 이벤트 ID
         * @param {string} customerId - 고객 ID
         * @param {Function} success - 성공 시 콜백
         * @param {Function} fail - 실패 시 콜백
         */
        resetParticipationInfo: async (eventId, customerId, success, fail) => {
            $store.commit('setIsLoading', true);

            return $apiInstance.post(`/events-v2/attendees/${eventId}/attendee/${customerId}/reset-participation`, {})
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.error('참여정보 초기화 실패:', error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        }
    };
}

/**
 * 임시 목업 템플릿 데이터 생성
 * 실제 API가 개발되면 제거될 함수
 */
function getMockTemplates(params = {}) {
    const allTemplates = [
        {
            id: 'template-kakao-event-1',
            name: '이벤트 안내 (카카오)',
            channel: 'kakao',
            type: 'event',
            content: '안녕하세요, #{name}님!\n\n새로운 이벤트가 시작되었습니다.\n지금 바로 확인하고 참여해보세요!\n\n이벤트 기간: #{startDate} ~ #{endDate}',
            buttons: [
                { name: '이벤트 확인하기', url: 'https://example.com/event/123' }
            ],
            createdAt: '2023-05-10T08:30:00Z'
        },
        {
            id: 'template-kakao-notification-1',
            name: '인증서 발급 안내 (카카오)',
            channel: 'kakao',
            type: 'notification',
            content: '#{name}님의 인증서가 발급되었습니다.\n\n발급일: #{issueDate}\n인증번호: #{certNo}\n\n인증서를 확인하시려면 아래 버튼을 눌러주세요.',
            buttons: [
                { name: '인증서 확인하기', url: 'https://example.com/cert/view' }
            ],
            createdAt: '2023-06-15T10:45:00Z'
        },
        {
            id: 'template-kakao-event-2',
            name: '이벤트 당첨 안내 (카카오)',
            channel: 'kakao',
            type: 'event',
            content: '축하합니다! #{name}님!\n\n#{eventName} 이벤트에 당첨되셨습니다.\n\n당첨 상품: #{prizeName}\n수령 방법은 아래 버튼을 통해 확인해주세요.',
            buttons: [
                { name: '당첨 정보 확인', url: 'https://example.com/winners' },
                { name: '배송지 입력하기', url: 'https://example.com/delivery' }
            ],
            createdAt: '2023-07-20T14:15:00Z'
        },
        {
            id: 'template-wechat-event-1',
            name: '活动通知 (위챗)',
            channel: 'wechat',
            type: 'event',
            content: '您好，#{name}！\n\n新活动已经开始。\n立即查看并参与！\n\n活动期间: #{startDate} ~ #{endDate}',
            buttons: [
                { name: '查看活动', url: 'https://example.com/event/123' }
            ],
            createdAt: '2023-08-05T09:20:00Z'
        },
        {
            id: 'template-wechat-notification-1',
            name: '证书发行通知 (위챗)',
            channel: 'wechat',
            type: 'notification',
            content: '#{name}的证书已发行。\n\n发行日期: #{issueDate}\n认证号码: #{certNo}\n\n点击下方按钮查看证书。',
            buttons: [
                { name: '查看证书', url: 'https://example.com/cert/view' }
            ],
            createdAt: '2023-09-12T11:30:00Z'
        },
        {
            id: 'template-kakao-certificate-1',
            name: '인증서 발급 완료 (카카오)',
            channel: 'kakao',
            type: 'certificate',
            content: '#{name}님, 인증서 발급이 완료되었습니다.\n\n• 인증서 번호: #{certNo}\n• 발급일: #{issueDate}\n• 유효기간: #{expireDate}\n\n인증서를 확인하시려면 아래 버튼을 눌러주세요.',
            buttons: [
                { name: '인증서 확인', url: 'https://example.com/certificate/view' }
            ],
            createdAt: '2023-10-25T15:40:00Z'
        },
        {
            id: 'template-wechat-certificate-1',
            name: '证书发行完成 (위챗)',
            channel: 'wechat',
            type: 'certificate',
            content: '#{name}，您的证书已发行完成。\n\n• 证书编号: #{certNo}\n• 发行日期: #{issueDate}\n• 有效期: #{expireDate}\n\n请点击下方按钮查看证书。',
            buttons: [
                { name: '查看证书', url: 'https://example.com/certificate/view' }
            ],
            createdAt: '2023-11-30T16:50:00Z'
        }
    ];

    // 필터링 적용
    let filteredTemplates = [...allTemplates];

    // 채널 필터
    if (params.channel) {
        filteredTemplates = filteredTemplates.filter(t => t.channel === params.channel);
    }

    // 타입 필터
    if (params.type) {
        filteredTemplates = filteredTemplates.filter(t => t.type === params.type);
    }

    // 키워드 검색
    if (params.keyword) {
        const keyword = params.keyword.toLowerCase();
        filteredTemplates = filteredTemplates.filter(t => 
            t.name.toLowerCase().includes(keyword) || 
            t.content.toLowerCase().includes(keyword)
        );
    }

    return filteredTemplates;
}
